import type { RouteParams } from '@/domains/core/routing/interfaces/routing';
import { routes } from '@/domains/core/routing/routes/routes.default';
import { createURL, isPage, parseURL } from '@/domains/core/routing/URLParser';

import type { PlainQueryParams } from '../interfaces/algolia';
import type { ListingPage } from '../model';
import { stringify } from './querystring';

export interface PageInfo {
  urlPattern: string;
  name: ListingPage;
  queryParams: RouteParams['queryParams'];
  pathParams: RouteParams['pathParams'];
}

export const getPageInfo = (url: string): PageInfo | undefined => {
  const [pathname] = url.split('?');
  if (isPage(routes.search, pathname)) {
    const { queryParams, pathParams } = parseURL(url, routes.search);
    return {
      name: 'search',
      urlPattern: routes.search,
      queryParams,
      pathParams,
    };
  }

  if (isPage(routes.category, pathname)) {
    const { queryParams, pathParams } = parseURL(url, routes.category);
    return {
      name: 'category',
      urlPattern: routes.category,
      queryParams,
      pathParams,
    };
  }

  if (isPage(routes.commercialOperationListing, pathname)) {
    const { queryParams, pathParams } = parseURL(
      url,
      routes.commercialOperationListing,
    );
    return {
      name: 'commercialOperationListing',
      urlPattern: routes.commercialOperationListing,
      queryParams,
      pathParams,
    };
  }

  if (isPage(routes.brand, pathname)) {
    const { queryParams, pathParams } = parseURL(url, routes.brand);
    return {
      name: 'brand',
      urlPattern: routes.brand,
      queryParams,
      pathParams,
    };
  }

  if (isPage(routes.massLanding, pathname)) {
    const { queryParams, pathParams } = parseURL(url, routes.massLanding);
    return {
      name: 'massLanding',
      urlPattern: routes.massLanding,
      queryParams,
      pathParams,
    };
  }

  if (isPage(routes.goodDeals, pathname)) {
    const { queryParams, pathParams } = parseURL(url, routes.goodDeals);
    return {
      name: 'goodDeals',
      urlPattern: routes.goodDeals,
      queryParams,
      pathParams,
    };
  }

  return undefined;
};

const checkParametersToKeep = (
  queryParams: PlainQueryParams,
  paramsToKeep: string[],
) => {
  let paramsToReturn = {};

  if (paramsToKeep.length) {
    paramsToKeep.map((param) => {
      if (Object.keys(queryParams).includes(param)) {
        paramsToReturn = {
          ...paramsToReturn,
          [param]: queryParams[param],
        };
      }
    });
  }

  return paramsToReturn;
};

export const getMandatoryQueryParamsByPageName = (
  pageName: ListingPage,
  queryParams: PlainQueryParams,
): PlainQueryParams => {
  let paramsToKeep: string[] = [];

  switch (pageName) {
    case 'commercialOperationListing':
      paramsToKeep = ['category_id'];
      break;
    case 'category':
      paramsToKeep = ['model_id'];
      break;
    default:
  }

  return checkParametersToKeep(queryParams, paramsToKeep);
};

export const createCommercialOperationListingCategoryURL = ({
  type,
  slug,
  id,
  categoryId,
}: {
  type: string;
  slug: string;
  id: number;
  categoryId: number;
  page?: number;
}): string =>
  `${createURL(routes.commercialOperationListing, {
    type,
    slug,
    id,
  })}?category_id=${categoryId}`;

export const createCommercialOperationListingURL = ({
  type,
  slug,
  id,
  queryParams,
}: {
  type: string;
  slug: string;
  id: number;
  queryParams?: PlainQueryParams;
}): string =>
  `${createURL(routes.commercialOperationListing, {
    type,
    slug,
    id,
  })}${stringify(queryParams || {})}`;

export const createSearchURL = ({
  query,
  queryParams,
}: {
  query: string;
  queryParams?: PlainQueryParams;
}): string =>
  `${createURL(routes.search, { query })}${stringify(queryParams || {})}`;

export const createListingURLByPageName = (
  pageName: ListingPage,
  pathParams: { [key: string]: string | string[] },
  queryParams?: PlainQueryParams,
): string => {
  switch (pageName) {
    case 'commercialOperationListing':
      return createCommercialOperationListingURL({
        id: parseInt(
          Array.isArray(pathParams.id) ? pathParams.id[0] : pathParams.id,
          10,
        ),
        slug: pathParams.slug as string,
        type: pathParams.type as string,
        queryParams,
      });
    case 'search':
      return createSearchURL({
        query: pathParams.query as string,
        queryParams,
      });

    default:
      throw new Error(
        'createListingURLByPageName should only used on listing page',
      );
  }
};
